import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaWarehouse, FaTimes, FaEdit } from "react-icons/fa";

const AddWarehouseModal = ({
  onClose,
  onAddWarehouse,
  warehouseToEdit,
  isEditing,
  onUpdateWarehouse,
}) => {
  const [formData, setFormData] = useState({
    warehouseName: "",
    address: "",
    contactPerson: "",
    mobileNo: "",
    wagroupId: "",
  });

  // Update formData if in editing mode
  useEffect(() => {
    if (isEditing && warehouseToEdit) {
      setFormData({
        warehouseName: warehouseToEdit.warehouseName,
        address: warehouseToEdit.address,
        contactPerson: warehouseToEdit.contactPerson,
        mobileNo: warehouseToEdit.mobileNo,
        wagroupId: warehouseToEdit.wagroupId,
      });
    }
  }, [isEditing, warehouseToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("You must be logged in to add or edit a warehouse.");
      return;
    }

    try {
      let response;

      // If we are editing, make an API call to update the warehouse
      if (isEditing) {
        response = await fetch(
          buildApiUrl(`/api/warehouse/new-warehouse/${warehouseToEdit._id}`),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
          }
        );
      } else {
        // If we are adding, make an API call to create a new warehouse
        response = await fetch(
          buildApiUrl("/api/warehouse/create/warehousename"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
          }
        );
      }

      const result = await response.json();
      if (response.ok) {
        if (isEditing) {
          onUpdateWarehouse(result.data); // Update the warehouse in the list
        } else {
          onAddWarehouse(result.data); // Add the new warehouse
        }
        onClose(); // Close the modal
        toast.success(
          isEditing
            ? "Warehouse updated successfully!"
            : "Warehouse added successfully!"
        );
      } else {
        toast.error(result.message || "Failed to save warehouse");
      }
    } catch (error) {
      toast.error("An error occurred while saving the warehouse");
    }
  };

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-70 z-50 transition-opacity duration-300">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm mx-4 p-4 transform transition-all duration-300 scale-100">
          <div className="flex items-center justify-between border-b border-gray-200 pb-2">
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <FaEdit className="text-indigo-600" size={16} />
                  <h2 className="text-lg font-semibold text-gray-800">
                    Edit Warehouse
                  </h2>
                </>
              ) : (
                <>
                  <FaWarehouse className="text-indigo-600" size={16} />
                  <h2 className="text-lg font-semibold text-gray-800">
                    Add Warehouse
                  </h2>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes size={16} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-3 space-y-3">
            <div>
              <label
                htmlFor="warehouseName"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Warehouse Name
              </label>
              <input
                id="warehouseName"
                type="text"
                name="warehouseName"
                value={formData.warehouseName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="contactPerson"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Contact Person
              </label>
              <input
                id="contactPerson"
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="mobileNo"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Contact Person Mobile No.
              </label>
              <input
                id="mobileNo"
                type="text"
                name="mobileNo"
                value={formData.mobileNo}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="wagroupId"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                WA Group ID
              </label>
              <input
                id="wagroupId"
                type="text"
                name="wagroupId"
                value={formData.wagroupId}
                onChange={handleChange}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="address"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Warehouse Address
              </label>
              <input
                id="address"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>

            <div className="flex justify-end items-center pt-3 border-t border-gray-200 gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-2 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-800 transition-colors duration-200 flex items-center gap-1"
              >
                {isEditing ? (
                  <>
                    <FaEdit size={12} />
                    Update Warehouse
                  </>
                ) : (
                  <>
                    <FaWarehouse size={12} />
                    Add Warehouse
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default React.memo(AddWarehouseModal);
